<template>
  <div v-if="visible" class="article-mail-subscription">
    <section class="subscription-banner" :class="{ 'is-english': english }" :aria-label="english ? 'Email subscription' : '邮件订阅'">
      <button type="button" class="subscription-close" :aria-label="english ? 'Dismiss email subscription' : '关闭邮件订阅入口'" @click="visible = false">
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m2 2 16 16M18 2 2 18" /></svg>
      </button>
      <div class="subscription-content">
        <div class="subscription-copy">
          <p class="subscription-title">{{ english ? 'Want more in-depth research?' : '想要了解更多深度研究？' }}</p>
          <p>{{ english ? 'Subscribe for the latest insights from CF40 Research.' : '订阅邮件推送，及时获取CF40研究最新成果' }}</p>
        </div>
        <form class="subscription-form" novalidate @submit.prevent="continueSubscription">
          <div class="subscription-fields">
            <input ref="emailInput" v-model.trim="email" type="email" name="email" autocomplete="email" placeholder="Email" :aria-label="english ? 'Email address' : '邮箱地址'" />
            <button type="submit" class="subscription-submit" :disabled="navigating">{{ english ? 'Subscribe' : '订阅' }}</button>
          </div>
          <div class="subscription-consent">
            <label>
              <input ref="consentInput" v-model="accepted" type="checkbox" />
              <span>{{ english ? 'I have read and accept email updates and the' : '我已阅读并确认接受本网站的信息推送和' }}</span>
            </label>{{ english ? ' ' : '' }}
            <button type="button" class="subscription-policy" :disabled="policyLoading" @click="openPrivacy">{{ english ? 'Privacy Policy' : '隐私政策' }}</button><span>{{ english ? '.' : '。' }}</span>
          </div>
        </form>
      </div>
    </section>
    <service-agreement ref="agreement" />
  </div>
</template>

<script>
import ServiceAgreement from '@/components/serviceAgreement';
import { initConfig } from '@/api';
import { setMailSubscriptionDraft, takeMailSubscriptionDraft } from '@/utils/mailSubscriptionDraft';

export default {
  name: 'ArticleMailSubscription',
  components: { ServiceAgreement },
  props: { language: { type: String, default: 'cn' } },
  data() {
    return { visible: true, email: '', accepted: false, navigating: false, policyLoading: false };
  },
  computed: {
    english() { return this.language === 'en'; }
  },
  methods: {
    async continueSubscription() {
      if (this.navigating) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.$message.warning(this.english ? 'Please enter a valid email address.' : '请输入有效的邮箱地址');
        this.$refs.emailInput.focus();
        return;
      }
      if (!this.accepted) {
        this.$message.warning(this.english ? 'Please accept email updates and the Privacy Policy.' : '请先阅读并同意信息推送和隐私政策');
        this.$refs.consentInput.focus();
        return;
      }
      this.navigating = true;
      setMailSubscriptionDraft(this.email, this.language);
      try {
        await this.$router.push({ name: 'MailPush', params: { language: this.english ? 'english' : 'chinese' } });
      } catch (error) {
        takeMailSubscriptionDraft(this.language);
        this.$message.error(this.english ? 'Unable to open subscription settings. Please try again.' : '暂时无法打开订阅页，请重试');
      } finally {
        this.navigating = false;
      }
    },
    async openPrivacy() {
      if (this.policyLoading) return;
      this.policyLoading = true;
      try {
        const res = await initConfig({}, this.english);
        const key = this.english ? 'PRIVACY_CLAUSE_EN' : 'PRIVACY_CLAUSE';
        const policy = res && res.code === 200 && Array.isArray(res.data)
          ? res.data.find(item => item.config_key === key) : null;
        if (!policy || !policy.config_value || !policy.config_value.trim()) throw new Error('missing-policy');
        if (this.$refs.agreement) this.$refs.agreement.open(this.english ? 'Privacy Policy' : '隐私政策', policy.config_value);
      } catch (error) {
        if (this.visible) this.$message.warning(this.english ? 'Unable to load the Privacy Policy. Please try again.' : '隐私政策加载失败，请重试');
      } finally {
        this.policyLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.article-mail-subscription {
  // 保留文末占位：阅读时贴住视口底部，到达原位置后自然随页面滚动。
  position: sticky;
  bottom: 0;
  z-index: 100;
  font-family: HarmonyOS_Sans_Regular, Arial, sans-serif;
}
.subscription-banner {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 136px;
  box-sizing: border-box;
  padding: 30px 40px;
  background: #fa6501 url('~@/assets/mail-subscription/banner.webp') center / cover no-repeat;
  color: #fff;
  button, input { font: inherit; }
  button { cursor: pointer; }
  button:focus-visible, input:focus-visible { outline: 2px solid #222; outline-offset: 3px; }
}
.subscription-close {
  position: absolute;
  top: 16px;
  right: 18px;
  display: flex;
  padding: 3px;
  border: 0;
  background: transparent;
  color: #fff;
  svg { width: 18px; height: 18px; stroke: currentColor; stroke-width: 1.5; }
}
.subscription-content {
  display: grid;
  grid-template-columns: 438px minmax(0, 1fr);
  align-items: center;
  width: 750px;
  max-width: 100%;
  margin: 0 auto;
}
.subscription-copy {
  transform: translateY(6px);
  font-size: 17px;
  line-height: 30px;
  p { margin: 0; }
  .subscription-title { font-family: HarmonyOS_Bold, Arial, sans-serif; font-size: 30px; font-weight: 400; }
}
.is-english .subscription-title { font-size: 28px; }
.subscription-form { align-self: start; margin-top: -1px; }
.subscription-fields {
  display: flex;
  gap: 21px;
  input {
    box-sizing: border-box;
    width: 182px;
    min-width: 0;
    height: 31px;
    padding: 0 16px;
    border: 0;
    border-radius: 6px;
    background: #fff;
    color: #222;
    font-family: Arial, sans-serif;
    font-size: 14px;
    &::placeholder { color: #bbb; }
  }
  .subscription-submit {
    flex-shrink: 0;
    width: 107px;
    height: 31px;
    padding: 0;
    border: 0;
    border-radius: 73px;
    background: #222;
    color: #fff;
    font-size: 16px;
    &:hover { background: #3d3d3d; }
    &:disabled { cursor: wait; opacity: .7; }
  }
}
.subscription-consent {
  margin-top: 6px;
  font-size: 10px;
  line-height: 22px;
  label { cursor: pointer; }
  input { width: 10px; height: 10px; margin: 0 4px 0 0; accent-color: #222; vertical-align: middle; }
  .subscription-policy {
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    text-decoration: underline;
    &:disabled { cursor: wait; }
  }
}
@media screen and (max-width: 900px) {
  .subscription-banner { padding: 32px; }
  .subscription-content { grid-template-columns: minmax(0, 1fr) 310px; gap: 24px; }
  .subscription-copy { font-size: 14px; transform: none; .subscription-title { font-size: 18px; } }
}
@media screen and (max-width: 640px) {
  .subscription-content { grid-template-columns: minmax(0, 1fr); gap: 18px; }
  .subscription-fields { gap: 16px; input { flex: 1; } }
  .subscription-consent { font-size: 12px; }
}
@media print { .article-mail-subscription { display: none; } }
</style>
